import type { StateStorage } from 'zustand/middleware';

/**
 * Per-tab persistence for the workspace / tenant selection.
 *
 * The active selection lives in `sessionStorage`, which is isolated per browser
 * tab — so a user can drive a different workspace in each tab without one tab
 * clobbering another. Previously both stores persisted to `localStorage`, which
 * is shared across every tab of the same browser, so switching workspace in one
 * tab silently overwrote it in all the others.
 *
 * A `localStorage` mirror keeps the last-used selection so a brand-new tab still
 * opens where the user left off (and existing users' persisted `localStorage`
 * seeds their first tab seamlessly), then each tab diverges independently.
 *
 * Read order: this tab's `sessionStorage` → the shared `localStorage` mirror.
 * Write: both, so the mirror always reflects the most recent selection.
 *
 * Note: duplicated tabs (open-in-new-tab / Duplicate Tab) inherit the origin
 * tab's `sessionStorage`, so a deep link opened in a new tab keeps its own
 * workspace. Fully new tabs seed from the `localStorage` mirror.
 */

// Last-resort in-memory store when both Web Storage APIs are unavailable
// (private mode / sandboxed iframe / storage disabled).
const memoryFallback = new Map<string, string>();

const safeStorage = (kind: 'session' | 'local'): Storage | null => {
  try {
    if (typeof window === 'undefined') return null;
    return kind === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
};

export const perTabStorage = (): StateStorage => ({
  getItem: (name) => {
    const fromTab = safeStorage('session')?.getItem(name);
    if (fromTab != null) return fromTab;
    // Fresh tab: seed from the last-used selection persisted in localStorage.
    const fromMirror = safeStorage('local')?.getItem(name);
    if (fromMirror != null) return fromMirror;
    return memoryFallback.get(name) ?? null;
  },
  setItem: (name, value) => {
    const session = safeStorage('session');
    const local = safeStorage('local');
    session?.setItem(name, value);
    // Mirror so future fresh tabs seed from the most recent selection.
    local?.setItem(name, value);
    if (!session && !local) memoryFallback.set(name, value);
  },
  removeItem: (name) => {
    // Only clear this tab's active selection; keep the mirror as the last-used
    // default for new tabs.
    safeStorage('session')?.removeItem(name);
    memoryFallback.delete(name);
  },
});
